import { NavLink } from 'react-router-dom';
import URLS from 'URLS';
import { useTranslation } from 'react-i18next';

// Material UI Components
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

// Icons
import Facebook from 'assets/icons/social_media/facebook.svg?react';
import Instagram from 'assets/icons/social_media/instagram.svg?react';
import LinkedIn from 'assets/icons/social_media/linkedin.svg?react';
import YouTube from 'assets/icons/social_media/youtube.svg?react';
import LOGO from 'assets/icons/ECPlayLogo.svg';
import DARKLOGO from 'components/Player/assets/img/ecplaylogodark.png';

// Project components
import LangSelect from 'components/miscellaneous/LangSelect';
import { lightThemePlayer } from 'components/Player/utils/multiplePlayerApps';

// Repeated pattern: social media icon buttons in followLogos (same responsive size each iteration)
const SocialIconButton = styled(IconButton)<{ component?: string; href?: string; rel?: string; target?: string }>(({ theme }) => ({
  width: '70px',
  height: '70px',
  [theme.breakpoints.down('lg')]: {
    width: '50px',
    height: '50px',
  },
  [theme.breakpoints.down('md')]: {
    width: '45px',
    height: '45px',
  },
}));

function Footer() {
  const light = lightThemePlayer();
  const { t } = useTranslation(['common']);

  const textColor = light ? 'white' : 'text.primary';

  const socialMediaIconSx = (theme: import('@mui/material/styles').Theme) => ({
    width: '50px',
    height: '50px',
    fill: light ? 'white' : theme.palette.text.primary,
    [theme.breakpoints.down('lg')]: {
      width: '40px',
      height: '40px',
    },
    [theme.breakpoints.down('md')]: {
      width: '35px',
      height: '35px',
    },
  });

  const socialMediaLinks = [
    { name: 'Facebook', link: 'https://www.facebook.com/ecplaymusic', icon: Facebook },
    { name: 'YouTube', link: 'https://www.youtube.com/channel/UCNdX-a63crg33SVzqXqnn6g', icon: YouTube },
    { name: 'Instagram', link: 'https://www.instagram.com/ecplaymusic/', icon: Instagram },
    { name: 'LinkedIn', link: 'https://www.linkedin.com/company/ec-play/', icon: LinkedIn },
  ];

  const logoWidthCopyright = (
    <Box
      sx={(theme) => ({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        [theme.breakpoints.between('sm', 'lg')]: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-evenly',
        },
      })}>
      <Box alt='EC-Play Logo' component='img' src={LOGO} sx={{ height: 60, mb: '10px', display: light ? 'none' : 'block' }} />
      <Box alt='EC-Play Logo' component='img' src={DARKLOGO} sx={{ height: 60, mb: '10px', display: light ? 'block' : 'none' }} />
      <Box
        sx={(theme) => ({
          color: textColor,
          fontSize: '20px',
          textAlign: 'center',
          lineHeight: '50px',
          height: '50px',
          [theme.breakpoints.down('md')]: {
            fontSize: '16px',
          },
        })}>
        {'© EC-Play ' + new Date().getFullYear()}
      </Box>
    </Box>
  );

  const contact = (
    <Box
      sx={(theme) => ({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        [theme.breakpoints.down('lg')]: {
          width: '100%',
        },
      })}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          [theme.breakpoints.between('sm', 'lg')]: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            gap: theme.spacing(4),
          },
        })}>
        <div>
          <Typography
            sx={(theme) => ({
              color: textColor,
              [theme.breakpoints.down('lg')]: {
                mt: 0,
              },
            })}
            variant={'h2'}>
            {t('FOOTER.CONTACT.HEADER')}
          </Typography>
        </div>
        <Box
          sx={(theme) => ({
            [theme.breakpoints.between('sm', 'lg')]: {
              mt: theme.spacing(2),
            },
          })}>
          <Typography sx={{ color: textColor }}>
            {`${t('FOOTER.CONTACT.PHONE')}: `}
            <Link href='tel:+4722501200' underline='hover'>
              +4722501200
            </Link>
          </Typography>
          <Typography sx={{ color: textColor }}>
            {`${t('FOOTER.CONTACT.EMAIL')}: `}
            <Link href='mailto:hei@ec-play.com' underline='hover'>
              hei@ec-play.com
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const followText = (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'start', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
        <Typography sx={{ color: textColor }} variant={'h2'}>
          {t('FOOTER.FOLLOW.HEADER')}
        </Typography>
        {socialMediaLinks.map((socialMedia) => (
          <Link href={socialMedia.link} key={socialMedia.name} rel='noopener noreferrer' target='_blank' underline={'hover'}>
            {socialMedia.name}
          </Link>
        ))}
      </Box>
    </Box>
  );

  const followLogos = (
    <Box
      sx={(theme) => ({
        width: '400px',
        display: 'flex',
        justifyContent: 'space-around',
        [theme.breakpoints.down('lg')]: {
          width: '300px',
          margin: 'auto',
        },
        [theme.breakpoints.down('md')]: {
          margin: 'auto',
          width: '250px',
        },
      })}>
      {socialMediaLinks.map((socialMedia) => {
        const Icon = socialMedia.icon;
        return (
          <SocialIconButton
            aria-label={socialMedia.name}
            component='a'
            href={socialMedia.link}
            key={socialMedia.name}
            rel='noopener noreferrer'
            target='_blank'>
            <Icon style={{ width: '100%', height: '100%', fill: light ? 'white' : '#111111' }} />
          </SocialIconButton>
        );
      })}
    </Box>
  );

  const privacyAndLanguage = (
    <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
      <LangSelect
        sx={(theme: import('@mui/material/styles').Theme) => ({
          color: light ? 'white' : theme.palette.text.primary,
          borderColor: light ? 'white' : theme.palette.text.primary,
        })}
      />
      <NavLink style={{ color: light ? 'white' : '#111111', textDecoration: 'none' }} to={URLS.PRIVACY}>
        {t('PRIVACY.PRIVACY')}
      </NavLink>
    </Box>
  );

  // TODO: Add back this text with functionality to subscribe to newsletter
  const newsletter = <></>;
  /*
  (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Typography sx={{ color: textColor, textAlign: 'center' }} variant={'h2'}>
        {t('FOOTER.NEWSLETTER.HEADER')}
      </Typography>
    </Box>
  );
  */

  return (
    <>
      <Divider variant='middle' />
      <Box
        sx={(theme) => ({
          overflowX: 'hidden',
          alignItems: 'start',
          width: '100%',
          p: 5,
          display: 'grid',
          gap: theme.spacing(2),
          gridTemplateColumns: '1fr 1fr 1fr 1fr',
          margin: 'auto',
          [theme.breakpoints.down('lg')]: {
            gridTemplateColumns: '1fr',
          },
        })}>
        <Box sx={{ display: { xs: 'none', lg: 'contents' } }}>
          {logoWidthCopyright}
          {contact}
          {followText}
          <div>
            {newsletter}
            {privacyAndLanguage}
          </div>
        </Box>
        <Box sx={{ display: { xs: 'contents', lg: 'none' } }}>
          {newsletter}
          {contact}
          {logoWidthCopyright}
          {followLogos}
          {privacyAndLanguage}
        </Box>
      </Box>
    </>
  );
}

export default Footer;
